import React, { useCallback, useEffect, useRef } from "react"

import { TEST_IDS } from "~config/constants"
import { ROUTES, navigateToRoute } from "~utils/helpers/navigation-helpers"
import useAppPrompts from "~components/shared/app-prompts/use-app-prompts"
import AppPromptModal from "~components/shared/app-prompts/app-prompt-modal"

export const SESSION_KEY = "MB_APP_PROMPT_ACTIVE"

const EXEMPT_ROUTES = [
    ROUTES.BILLING,
    ROUTES.BILLING_PORTAL,
    ROUTES.ACCOUNT_PORTAL,
    ROUTES.ONBOARD,
    ROUTES.TEAM_MEMBER_ONBOARDING,
    ROUTES.APP_PROMPT_BLOCKING,
]

export default function AppPromptGate() {
    const { activePrompt, isBlocking, dismiss } = useAppPrompts()
    const isExemptRoute = useIsExemptRoute()
    const hasRedirected = useRef(false)

    useBlockingRedirect({
        activePrompt,
        isBlocking,
        isExemptRoute,
        hasRedirected,
    })

    const handleDismiss = useCallback(() => {
        if (!activePrompt) return
        dismiss(activePrompt.id, activePrompt.dismiss_for_seconds ?? null)
    }, [activePrompt, dismiss])

    if (!activePrompt || isExemptRoute) return null
    if (isBlocking) return null

    return (
        <div data-testid={TEST_IDS.APP_PROMPT_GATE}>
            <AppPromptModal prompt={activePrompt} onDismiss={handleDismiss} />
        </div>
    )
}

function useIsExemptRoute() {
    if (typeof window === "undefined") return true
    const { pathname } = window.location

    return EXEMPT_ROUTES.some((route) => pathname.startsWith(route))
}

function useBlockingRedirect({
    activePrompt,
    isBlocking,
    isExemptRoute,
    hasRedirected,
}: {
    readonly activePrompt: ReturnType<typeof useAppPrompts>["activePrompt"]
    readonly isBlocking: boolean
    readonly isExemptRoute: boolean
    readonly hasRedirected: React.MutableRefObject<boolean>
}) {
    useEffect(() => {
        if (!activePrompt || !isBlocking || isExemptRoute) {
            hasRedirected.current = false
            return
        }

        if (hasRedirected.current) return
        hasRedirected.current = true

        sessionStorage.setItem(SESSION_KEY, JSON.stringify(activePrompt))
        void navigateToRoute(ROUTES.APP_PROMPT_BLOCKING, { replace: true })
    }, [activePrompt, isBlocking, isExemptRoute, hasRedirected])
}
